import Decimal from 'decimal.js-light';
import type { Infer, Struct } from 'superstruct';
import {
	coerce,
	defaulted,
	dynamic,
	never,
	nullable,
	number,
	object,
	optional,
	partial,
	refine,
	string,
} from 'superstruct';

import {
	emptyStringIsNull,
	parsableInteger,
	stringDate,
	userString,
	userStringWithMaxLength,
	withMessage,
} from '@adflow/shared/util-superstruct';

export const ViewPromotionAssetDto = object({
	id: number(),
	name: string(),
	occasion: optional(string()),
	language: optional(string()),
	currency: optional(string()),
	promoType: optional(string()),
	promoValue: optional(number()),
	item: optional(string()),
	finalUrl: optional(string()),
	details: optional(string()),
	ordersOverValue: optional(number()),
	promoCode: optional(string()),
	barcode: optional(string()),
	barcodeType: optional(string()),
	qrCode: optional(string()),
	startDate: optional(nullable(stringDate())),
	endDate: optional(nullable(stringDate())),
	promotionStartDate: optional(nullable(stringDate())),
	promotionEndDate: optional(nullable(stringDate())),

	createdAt: stringDate(),
	updatedAt: stringDate(),
});

function detailsValidation<T, V>(
	defaultStruct: Struct<T, null>,
	validatedStruct: Struct<V, null>,
	detailTypes: (
		| 'NONE'
		| 'ON_ORDERS_OVER'
		| 'PROMO_CODE'
		| 'PROMO_CODE_BARCODE'
		| 'PROMO_CODE_QR'
	)[]
): Struct<T | V, null> {
	return dynamic<T | V>((_value, context) => {
		const contextValues = context.branch.at(-2);
		if (detailTypes.includes(contextValues.details)) {
			return validatedStruct;
		}

		return defaultStruct;
	});
}

export const AddPromotionAssetDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	occasion: userString('validations:Required'),
	language: userString('validations:Required'),
	currency: userString('validations:Required'),
	promoType: userString('validations:Required'),
	promoValue: dynamic((value, context) => {
		if (!value) {
			return withMessage(never(), 'validations:Required');
		}

		const contextValues = context.branch.at(-2);
		const promoType = contextValues?.promoType;

		const coercedNumber = coerce(number(), string(), (val) =>
			val ? new Decimal(val).toNumber() : 0
		);

		if (typeof promoType === 'string' && promoType.startsWith('PERCENT')) {
			return withMessage(
				refine(coercedNumber, 'promoValue', (val) => {
					return val > 0 && val <= 100 && Number.isInteger(val);
				}),
				'validations:Promotion percent'
			);
		}

		return withMessage(
			refine(coercedNumber, 'promoValue', (val) => {
				return val > 0;
			}),
			'validations:Quantity Above 0'
		);
	}),
	item: userStringWithMaxLength(20),
	finalUrl: userString('validations:Required'),
	details: defaulted(string(), 'NONE'),
	ordersOverValue: detailsValidation(
		optional(emptyStringIsNull(number())),
		withMessage(
			coerce(number(), string(), (value) =>
				value ? new Decimal(value).toNumber() : 0
			),
			'validations:Required'
		),
		['ON_ORDERS_OVER']
	),
	promoCode: detailsValidation(
		optional(string()),
		userStringWithMaxLength(15),
		['PROMO_CODE']
	),
	barcode: detailsValidation(
		optional(string()),
		userString('validations:Required'),
		['PROMO_CODE_BARCODE']
	),
	barcodeType: detailsValidation(
		optional(string()),
		userString('validations:Required'),
		['PROMO_CODE_BARCODE']
	),
	qrCode: detailsValidation(optional(string()), userStringWithMaxLength(720), [
		'PROMO_CODE_QR',
	]),
	startDate: dynamic((value, context) => {
		if (value && typeof value === 'string') {
			const contextValues = context.branch.at(-2);
			const endDate = contextValues.endDate;
			if (endDate && typeof endDate === 'string') {
				const startDateObj = new Date(value);
				const endDateObj = new Date(endDate);
				if (
					!Number.isNaN(startDateObj.getTime()) &&
					!Number.isNaN(endDateObj.getTime())
				) {
					const isGreater = startDateObj > endDateObj;
					if (isGreater) {
						return withMessage(
							never(),
							'validations:DateTime startDateTimeBeforeEnd'
						);
					}
				}
			}
		}
		return optional(nullable(stringDate()));
	}),
	endDate: optional(nullable(stringDate())),
	promotionStartDate: dynamic((value, context) => {
		if (value && typeof value === 'string') {
			const contextValues = context.branch.at(-2);
			const promotionEndDate = contextValues.promotionEndDate;
			if (promotionEndDate && typeof promotionEndDate === 'string') {
				const promotionStartDateObj = new Date(value);
				const promotionEndDateObj = new Date(promotionEndDate);
				if (
					!Number.isNaN(promotionStartDateObj.getTime()) &&
					!Number.isNaN(promotionEndDateObj.getTime())
				) {
					const isGreater = promotionStartDateObj > promotionEndDateObj;
					if (isGreater) {
						return withMessage(
							never(),
							'validations:DateTime startDateTimeBeforeEnd'
						);
					}
				}
			}
		}
		return withMessage(stringDate(), 'validations:Required');
	}),
	promotionEndDate: withMessage(stringDate(), 'validations:Required'),
});

export const EditPromotionAssetDto = partial(AddPromotionAssetDto);

export type ViewPromotionAssetDto = Infer<typeof ViewPromotionAssetDto>;
export type AddPromotionAssetDto = Infer<typeof AddPromotionAssetDto>;
export type EditPromotionAssetDto = Infer<typeof EditPromotionAssetDto>;
